import React, { useState, useRef, useEffect } from "react";
import { LoadScriptNext, Autocomplete } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import { createSalesmanCustomer } from "../../redux/actions/salesmanAction";
import {
  fetchCustomerTypes,
  fetchKarnatakaCities,
} from "../../redux/actions/customerActions";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import { FullScreenLoader } from "../Components/Loader";
import {
  FiMapPin,
  FiUser,
  FiPhone,
  FiMail,
  FiMap,
  FiTag,
  FiHome,
  FiCheckCircle,
  FiX,
  FiAward,
} from "react-icons/fi";
import CitySingleSelectDropdown from "../Components/CitySingleSelectDropdown";
const libraries = ["places"];

const CreateCustomer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.salesman || {});
  const { customerTypes = [], loading: customerTypesLoading = false } =
    useSelector((state) => state.fetchCustomerList || {});
  const { karnatakaCities = [], citiesLoading = false } = useSelector(
    (state) => state.fetchCustomerList || {},
  );

  const [isAddressFetched, setIsAddressFetched] = useState(false);
  const [disableCity, setDisableCity] = useState(false);
  const [disableState, setDisableState] = useState(false);
  const [disablePincode, setDisablePincode] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const autocompleteRef = useRef(null);
  const [cityError, setCityError] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    firm_name: "",
    customer_type: "",
    mobile: "",
    alternate_mobile: "",
    email: "",
    address: "",
    city: "",
    state: "Karnataka",
    pincode: "",
    landmark: "",
    lat: "",
    long: "",
    customer_grade: "",
  });

  // State for success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [generatedCustomerCode, setGeneratedCustomerCode] = useState("");

  // Fetch customer types on component mount
  useEffect(() => {
    if (userInfo?.access_token) {
      dispatch(fetchCustomerTypes(userInfo.access_token));
      dispatch(fetchKarnatakaCities(userInfo.access_token));
    }
  }, [dispatch, userInfo?.access_token]);
  // Debug: Log customer types when they change
  useEffect(() => {
    console.log("Customer Types in component:", customerTypes);
    console.log("Customer Types length:", customerTypes?.length);
    console.log("Customer Types Loading:", customerTypesLoading);
  }, [customerTypes, customerTypesLoading]);

  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current?.getPlace();

    if (place) {
      let address = "";
      let city = "";
      let state = "";
      let pincode = "";
      let lat = "";
      let long = "";

      // Extract coordinates
      if (place.geometry && place.geometry.location) {
        lat = place.geometry.location.lat().toString();
        long = place.geometry.location.lng().toString();
      }

      // Extract address components
      place.address_components?.forEach((component) => {
        const types = component.types;
        if (types.includes("locality")) {
          city = component.long_name;
        }
        if (types.includes("administrative_area_level_1")) {
          state = component.long_name;
        }
        if (types.includes("postal_code")) {
          pincode = component.long_name;
        }
      });

      address = place.formatted_address || "";

      // Update the formData state with the extracted values
      setFormData((prev) => ({
        ...prev,
        address,
        city,
        state,
        pincode,
        lat,
        long,
      }));
      setDisableCity(city !== "");
      setDisableState(state !== "");
      setDisablePincode(pincode !== "");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      const cleanedValue = value.replace(/\D/g, "");
      if (/^[6-9]\d{0,9}$/.test(cleanedValue) || cleanedValue === "") {
        setFormData((prev) => ({
          ...prev,
          [name]: cleanedValue,
          // Auto-fill alternate_mobile with the same value
          alternate_mobile: cleanedValue,
        }));
      }
    } else if (name === "alternate_mobile") {
      const cleanedValue = value.replace(/\D/g, "");
      if (/^[6-9]\d{0,9}$/.test(cleanedValue) || cleanedValue === "") {
        setFormData((prev) => ({
          ...prev,
          [name]: cleanedValue,
        }));
      }
    } else if (name === "pincode") {
      const cleanedValue = value.replace(/\D/g, "");
      if (cleanedValue.length <= 6) {
        setFormData((prev) => ({
          ...prev,
          [name]: cleanedValue,
        }));
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (formData.mobile.length !== 10) {
      toast.error("Mobile number must be 10 digits");
      return;
    }

    if (formData.alternate_mobile.length !== 10) {
      toast.error("WhatsApp number must be 10 digits");
      return;
    }

    if (formData.pincode && formData.pincode.length !== 6) {
      toast.error("Pincode must be 6 digits");
      return;
    }
    if (!formData.city.trim()) {
      setCityError(true);
      toast.error("Please select a city");
      return;
    }
    setCityError(false);

    if (!formData.customer_type) {
      toast.error("Please select a customer type");
      return;
    }

    try {
      const result = await dispatch(
        createSalesmanCustomer(formData, userInfo.access_token),
      );

      if (result.type === "CREATE_SALESMAN_CUSTOMER_SUCCESS") {
        // Show success modal with customer code
        const customerCode =
          result.payload?.customer_code ||
          result.payload?.data?.customer_code ||
          "";
        setGeneratedCustomerCode(customerCode);
        setShowSuccessModal(true);
      } else {
        const errorMessage =
          result.payload?.message ||
          result.payload ||
          "Failed to create customer";
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error("Failed to create customer. Please try again.");
    }
  };

  return (
    <DashboardLayout>
      <FullScreenLoader
        show={loading}
        message={loading ? "Creating customer..." : ""}
        variant="primary"
        blurIntensity="md"
      />
      <LoadScriptNext
        googleMapsApiKey="AIzaSyBjwQjSQoYFe5OzJxbhnodl-usXAt6AH8g"
        libraries={libraries}
        onLoad={() => setMapReady(true)}
      >
        {mapReady ? (
          <div className="relative pt-4 px-3 sm:px-4">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <button
                onClick={() => navigate(-1)}
                className="hover:text-gray-800 transition-colors p-2 hover:bg-amber-50 rounded-lg"
              >
                <FaArrowLeft size={20} />
              </button>
              <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent">
                Create Customer
              </h2>
            </div>

            <div className="flex justify-center items-start relative z-10 pb-8">
              {/* Form Container */}
              <div className="w-full max-w-4xl">
                <form
                  onSubmit={handleSubmit}
                  className="relative z-10 bg-white border-2 border-amber-500 rounded-2xl p-4 sm:p-6 shadow-2xl space-y-4 sm:space-y-6"
                >
                  {/* Step 1: Basic Information */}
                  <div className="bg-gradient-to-br from-amber-50/30 to-white border-2 border-amber-200 rounded-xl p-4 sm:p-5">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-950 mb-3 sm:mb-4 flex items-center gap-2">
                      <span className="text-lg sm:text-xl">👤</span>
                      Step 1: Basic Information
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {/* Name */}
                      <div className="relative">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition text-sm sm:text-base"
                            placeholder="Enter customer name"
                            required
                          />
                        </div>
                      </div>

                      {/* Firm Name - Optional */}
                      <div className="relative">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          Firm Name
                        </label>
                        <div className="relative">
                          <FiHome className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                          <input
                            type="text"
                            id="firm_name"
                            name="firm_name"
                            value={formData.firm_name}
                            onChange={handleChange}
                            className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition text-sm sm:text-base"
                            placeholder="Enter firm name (optional)"
                          />
                        </div>
                      </div>

                      {/* Customer Type */}
                      <div className="relative">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          Customer Type <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FiTag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none z-10" />
                          <select
                            id="customer_type"
                            name="customer_type"
                            value={formData.customer_type}
                            onChange={handleChange}
                            className="w-full pl-9 sm:pl-10 pr-10 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition appearance-none bg-white text-sm sm:text-base"
                            disabled={customerTypesLoading}
                            required
                          >
                            <option value="">
                              {customerTypesLoading
                                ? "Loading..."
                                : "Select Customer Type"}
                            </option>
                            {customerTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <svg
                              className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="relative">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          Customer Grade <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FiAward className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none z-10" />
                          <select
                            id="customer_grade"
                            name="customer_grade"
                            value={formData.customer_grade}
                            onChange={handleChange}
                            className="w-full pl-9 sm:pl-10 pr-10 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition appearance-none bg-white text-sm sm:text-base"
                            required
                          >
                            <option value="">Select Grade</option>
                            <option value="A">A</option>
                            <option value="B">B</option>
                            <option value="C">C</option>
                          </select>
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <svg
                              className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Contact Information */}
                  <div className="bg-gradient-to-br from-blue-50/30 to-white border-2 border-blue-200 rounded-xl p-4 sm:p-5">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-950 mb-3 sm:mb-4 flex items-center gap-2">
                      <span className="text-lg sm:text-xl">📞</span>
                      Step 2: Contact Information
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {/* Mobile */}
                      <div className="relative">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          Mobile <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                          <input
                            type="tel"
                            id="mobile"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition text-sm sm:text-base"
                            placeholder="Enter 10-digit mobile number"
                            maxLength="10"
                            required
                          />
                        </div>
                      </div>

                      {/* WhatsApp Number */}
                      <div className="relative">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          WhatsApp Number{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                          <input
                            type="tel"
                            id="alternate_mobile"
                            name="alternate_mobile"
                            value={formData.alternate_mobile}
                            onChange={handleChange}
                            className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition text-sm sm:text-base"
                            placeholder="Enter WhatsApp number"
                            maxLength="10"
                            required
                          />
                          {formData.alternate_mobile &&
                            formData.alternate_mobile === formData.mobile && (
                              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-amber-600 font-medium">
                                Auto-filled
                              </span>
                            )}
                        </div>
                        {formData.mobile && (
                          <p className="mt-1 text-xs text-gray-500">
                            Auto-filled from mobile number
                          </p>
                        )}
                      </div>

                      {/* Email - Moved after WhatsApp */}
                      <div className="relative sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <div className="relative">
                          <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition text-sm sm:text-base"
                            placeholder="Enter email address (optional)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 3: Address Information */}
                  <div className="bg-gradient-to-br from-green-50/30 to-white border-2 border-green-200 rounded-xl p-4 sm:p-5">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-950 mb-3 sm:mb-4 flex items-center gap-2">
                      <span className="text-lg sm:text-xl">📍</span>
                      Step 3: Address Information
                    </h3>

                    <div className="space-y-3 sm:space-y-4">
                      {/* Address Autocomplete */}
                      <div className="relative">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          Address <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Autocomplete
                            onLoad={(ref) => (autocompleteRef.current = ref)}
                            onPlaceChanged={() => {
                              handlePlaceChanged();
                              setIsAddressFetched(true);
                            }}
                          >
                            <input
                              type="text"
                              name="address"
                              value={formData.address}
                              onChange={(e) => {
                                handleChange(e);
                                setIsAddressFetched(false);
                              }}
                              placeholder="Start typing address..."
                              className="w-full pl-10 sm:pl-12 pr-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition text-sm sm:text-base"
                              required
                            />
                          </Autocomplete>
                          <FiMapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-600 w-4 h-4 sm:w-5 sm:h-5" />
                        </div>
                        {isAddressFetched && (
                          <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                            <span>✓</span> Address details auto-filled
                          </p>
                        )}
                      </div>

                      {/* Landmark */}
                      <div className="relative">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          Landmark
                        </label>
                        <div className="relative">
                          <FiMapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                          <input
                            type="text"
                            id="landmark"
                            name="landmark"
                            value={formData.landmark}
                            onChange={handleChange}
                            className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition text-sm sm:text-base"
                            placeholder="Enter landmark (optional)"
                          />
                        </div>
                      </div>

                      {/* State */}
                      <div className="relative">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          State <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FiMap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                          <input
                            type="text"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            disabled={isAddressFetched ? disableState : true}
                            placeholder="State"
                            className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                            required
                          />
                        </div>
                      </div>

                      {/* City and Pincode in same row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {/* City */}
                        <div className="relative">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                            City <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            {isAddressFetched ? (
                              <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                disabled={disableCity}
                                placeholder="City"
                                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                                required
                              />
                            ) : (
                              <div>
                                <div>
                                  <div
                                    className={
                                      cityError
                                        ? "ring-2 ring-red-400 rounded-lg"
                                        : ""
                                    }
                                  >
                                    <CitySingleSelectDropdown
                                      cities={karnatakaCities.map((c) =>
                                        typeof c === "string" ? c : c.name,
                                      )}
                                      selected={formData.city}
                                      loading={citiesLoading}
                                      placeholder="Select City"
                                      allowClear={false}
                                      onChange={(city) => {
                                        setFormData((prev) => ({
                                          ...prev,
                                          city,
                                        }));
                                        setCityError(false);
                                      }}
                                    />
                                  </div>
                                  {cityError && (
                                    <p className="mt-1 text-xs text-red-500">
                                      City is required
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Pincode */}
                        <div className="relative">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                            Pincode <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              name="pincode"
                              value={formData.pincode}
                              onChange={handleChange}
                              disabled={disablePincode}
                              placeholder="6-digit pincode"
                              maxLength="6"
                              className="w-full pl-4 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-row justify-end gap-3 sm:gap-4 mt-4 sm:mt-6 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="flex items-center justify-center bg-white border-2 border-red-500 text-red-600 py-2.5 px-4 sm:px-6 rounded-lg hover:bg-red-50 font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <FiX size={20} className="sm:mr-2" />
                      <span className="hidden sm:inline">Cancel</span>
                    </button>

                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2.5 px-4 sm:px-6 rounded-lg hover:from-amber-600 hover:to-amber-700 font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {loading ? "Creating..." : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center min-h-screen">
            <p className="text-gray-500 text-lg">Loading Google Maps...</p>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative">
              {/* Close Button */}
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate("/dashboard/salesmancustomerlist");
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX size={24} />
              </button>

              {/* Success Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                  <FiCheckCircle className="text-white" size={48} />
                </div>
              </div>

              {/* Success Message */}
              <div className="text-center mb-6">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Congratulations!
                </h3>
                <p className="text-gray-600 text-sm sm:text-base mb-4">
                  Customer created successfully!
                </p>

                {/* Customer Code Display */}
                {generatedCustomerCode && (
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300 rounded-xl p-4 sm:p-5 mt-4">
                    <p className="text-xs sm:text-sm text-gray-600 mb-2">
                      Your Customer Code:
                    </p>
                    <p className="text-2xl sm:text-3xl font-bold text-amber-700 tracking-wider">
                      {generatedCustomerCode}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate("/dashboard/salesmancustomerlist");
                }}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3 px-6 rounded-lg hover:from-amber-600 hover:to-amber-700 font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                View Customer List
              </button>
            </div>
          </div>
        )}
      </LoadScriptNext>
    </DashboardLayout>
  );
};

export default CreateCustomer;
